import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { ChangeInputHandler } from "../../../../helpers/functions/types";
import { StyleVariant } from "../types";
import { Disabled } from "../../../../components/Disabled";
import {
  INPUT_DEFAULT_STYLES,
  SM_LONG_INPUT_STYLES,
} from "../../detailedPages/styles";
import { useInputValidity } from "../../detailedPages/ValidationContext";

const ATOM_TEXT_INPUT_STYLES: Record<StyleVariant, string | undefined> = {
  newPage: `h-9 ${INPUT_DEFAULT_STYLES} ${SM_LONG_INPUT_STYLES}`,
  detailPage: INPUT_DEFAULT_STYLES,
  slideOver: `xs:max-w-[50%] ${INPUT_DEFAULT_STYLES}`,
  srLabel: `${INPUT_DEFAULT_STYLES} relative border-none bg-stone-100 px-3 py-2 text-sm wrap-break-word min-h-fit`,
};

export const TextInputAtom: FC<{
  name: string;
  value?: string;
  onChange?: ChangeInputHandler;
  capitalize?: boolean;
  permissions?: string;
  disabled?: boolean;
  required?: boolean;
  styleVariant: StyleVariant;
  type?: React.HTMLInputTypeAttribute;
  textArea?: boolean;
  pattern?: string;
  placeholder?: string;
}> = ({
  name,
  value,
  onChange,
  permissions,
  disabled,
  required,
  styleVariant,
  type,
  textArea,
  pattern,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useInputValidity(inputRef);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (target: HTMLInputElement | HTMLTextAreaElement) => {
    setInputValue(target.value);
    onChange?.(target);
  };

  const InputNode = textArea ? (
    <textarea
      name={name}
      id={name}
      value={inputValue}
      disabled={disabled}
      required={required}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
        handleChange(event.target)
      }
      className={`${ATOM_TEXT_INPUT_STYLES[styleVariant]} py-2 disabled:cursor-not-allowed`}
      rows={3}
    />
  ) : (
    <input
      ref={inputRef}
      name={name}
      id={name}
      value={inputValue}
      type={type}
      placeholder={
        placeholder ?? (type === "url" ? "https://example.com" : " ")
      }
      pattern={pattern}
      disabled={disabled}
      required={required}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        handleChange(event.target)
      }
      className={`${ATOM_TEXT_INPUT_STYLES[styleVariant]} cursor-text disabled:cursor-not-allowed`}
    />
  );

  return permissions ? (
    <Disabled editPermissions={permissions}>{InputNode}</Disabled>
  ) : (
    InputNode
  );
};
